<template>
    <l-card-content data-test-id="survey-edit-status">
        <l-headline
            :tabs="tabs"
            :title="$t('surveys.details.titleStatus')"
            :sub-title="survey.name ? survey.name : $t('surveys.details.labelNewSurvey')"
            :auto-save-text="autoSavingText"
            btn-test-id="surveys-edit-status"
        >
        </l-headline>

        <hr class="visible-xs" />
        <div v-if="survey.status == 'draft' && survey.setupComplete">
            <div class="row">
                <div class="col-md-9">
                    <p style="margin-top: 15px">
                        <span>{{ $t("surveys.details.helpPreviewSetup") }}</span>
                    </p>
                </div>
                <div class="col-md-3">
                    <button
                        data-test-id="survey-status-preview"
                        class="btn btn-primary btn-rounded-primary btn-block"
                        type="button"
                        @click="openPreview"
                    >
                        <span>{{ $t("surveys.details.btnPreviewSetup") }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="survey.status == 'draft' && survey.setupComplete">
            <div class="row">
                <div class="col-md-9">
                    <p style="margin-top: 15px">
                        <span
                            v-if="
                                ['ONCE_AFTER_START', 'ONCE_BEFORE_END'].includes(
                                    survey.timing.frequency,
                                )
                            "
                        >
                            {{ $t("surveys.details.helpConfirmSetupDynamic") }}
                        </span>
                        <span v-else>
                            <span>{{ $t("surveys.details.helpConfirmSetup") }}</span>
                            {{ survey.timing.nextPeriodStart | moment("MMM D, YYYY") }}!
                        </span>
                    </p>
                </div>
                <div class="col-md-3">
                    <button
                        data-test-id="survey-status-active"
                        class="btn btn-primary btn-raised btn-block"
                        type="button"
                        @click="showConfirmationModalActive = true"
                    >
                        <span>{{ $t("surveys.details.btnConfirmSetup") }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="survey.status == 'active'" class="row">
            <div class="col-md-9">
                <p style="margin-top: 15px">
                    <span>{{ $t("surveys.details.helpPause") }}</span>
                </p>
            </div>
            <div class="col-md-3">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    type="button"
                    data-test-id="survey-status-paused"
                    @click="updateStatus('paused')"
                >
                    <span>{{ $t("surveys.details.btnPause") }}</span>
                </button>
            </div>
        </div>

        <div v-if="survey.status == 'paused'" class="row">
            <div class="col-md-9">
                <p style="margin-top: 15px">
                    <span>{{ $t("surveys.details.helpResume") }}</span>
                </p>
            </div>
            <div class="col-md-3">
                <button
                    data-test-id="survey-status-resume"
                    class="btn btn-primary btn-raised btn-block"
                    type="button"
                    @click="updateStatus('active')"
                >
                    <span>{{ $t("surveys.details.btnResume") }}</span>
                </button>
            </div>
        </div>

        <div class="row">
            <div class="col-md-9">
                <p style="margin-top: 15px">
                    <span>{{ $t("surveys.details.helpRemoveSurvey") }}</span>
                </p>
            </div>
            <div class="col-md-3">
                <button
                    class="btn btn-block btn-rounded-danger btn-danger"
                    type="button"
                    @click="showConfirmationModalRemoved = true"
                >
                    <span>{{ $t("surveys.details.btnRemoveSurvey") }}</span>
                </button>
            </div>
        </div>

        <div v-if="survey.status == 'active' || survey.status == 'paused'" class="row">
            <div class="col-md-9">
                <p style="margin-top: 15px">
                    <span>{{ $t("surveys.details.helpClose") }}</span>
                </p>
            </div>
            <div class="col-md-3">
                <button
                    data-test-id="survey-status-closed"
                    class="btn btn-block btn-rounded-warning btn-warning"
                    type="button"
                    @click="updateStatus('closed')"
                >
                    <span>{{ $t("surveys.details.btnClose") }}</span>
                </button>
            </div>
        </div>

        <div v-if="survey.setupComplete" class="row">
            <div class="col-md-9">
                <p style="margin-top: 15px">
                    <span>{{ $t("surveys.details.helpCloneSurvey") }}</span>
                </p>
            </div>
            <div class="col-md-3">
                <button
                    data-test-id="survey-status-clone"
                    class="btn btn-block btn-rounded-primary btn-primary"
                    type="button"
                    @click="cloneSurvey()"
                >
                    <span>{{ $t("surveys.details.btnCloneSurvey") }}</span>
                </button>
            </div>
        </div>

        <l-confirmation-modal
            v-if="showConfirmationModalActive"
            :text="$t('surveys.details.helpSendSurveyModal')"
            @confirm="updateStatus('active')"
            @cancel="showConfirmationModalActive = false"
        />

        <l-confirmation-modal
            v-if="showConfirmationModalRemoved"
            :text="$t('surveys.details.helpRemoveSurveyModal')"
            @confirm="updateStatus('removed')"
            @cancel="showConfirmationModalRemoved = false"
        />
    </l-card-content>
</template>

<script>
import { mapGetters } from "vuex";
import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LConfirmationModal from "@/components/LConfirmationModal";

import icon from "@/img/icons/surveys.png";

export default {
    components: {
        LCardContent,
        LHeadline,
        LConfirmationModal,
    },
    data() {
        return {
            showConfirmationModalActive: false,
            showConfirmationModalRemoved: false,
            autoSavingText: "",
            survey: {},
        };
    },
    computed: {
        ...mapGetters({
            getDetail: "surveys/surveys/detail",
            user: "users/current/user",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: true,
                    label: this.$t("surveys.details.showHC"),
                    handler: this.openHelpHC,
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
            },
        },
    },

    created() {
        this.icon = icon;
    },
    methods: {
        updateStatus(newStatus) {
            this.autoSavingText = this.$t(`surveys.details.autoSaving`);
            const oldStatus = this.survey.status;
            this.$store
                .dispatch("surveys/surveys/updateStatus", {
                    payload: { newStatus, survey: this.survey },
                })
                .then(() => {
                    if ("removed" === newStatus) {
                        this.$router.push(`/surveys/list`);
                    }
                    if (oldStatus === "draft" && newStatus === "active") {
                        // created
                        this.$router.push(`/surveys/insights/${this.survey._id}/list`);
                    }
                })
                .finally(() => {
                    this.autoSavingText = "";
                    this.showConfirmationModalActive = false;
                    this.showConfirmationModalRemoved = false;

                    // "survey" is not linked to a reactive getter which is why we need to get
                    // the latest survey details after an update again or our UI will remain in
                    // a stale state.
                    this.survey = this.getDetail(this.survey._id);
                });
        },
        cloneSurvey() {
            this.$store
                .dispatch("surveys/surveys/clone", { payload: { survey: this.survey._id } })
                .then(e => {
                    if (e?.data?._id) {
                        this.$router.push(`/surveys/edit/${e.data._id}/basics`);
                    }
                });
        },
        openPreview() {
            const lang = this.user.lang || "en";
            window.open(
                `/app/#/surveys/${this.$route.params.id}/answer/preview?lang=${lang}`,
                "_blank",
                "noopener noreferrer",
            );
        },
        openHelpHC() {
            window.open(
                "https://leapsome.zendesk.com/hc/en-us/categories/360000286654-Surveys",
                "_blank",
                "noopener noreferrer",
            );
        },
        goBack() {
            this.showConfirmationModalActive = false;
        },
    },
};
</script>

<style scoped></style>
